import { Flex, Text, IconButton, Separator, Button } from "@radix-ui/themes";
import { PlusIcon } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { EscalationRuleOperationType } from "@/v2/entities/escalation-rule/EscalationRule.model";
import { EscalationRuleFormData } from "../../EscalationRuleForm";
import { useCallback, useMemo } from "react";
import { DropdownMenu } from "@/components/shared/dropdown-menu";

interface SubjectSectionProps {
  value: EscalationRuleFormData;
  onChange: (updates: Partial<EscalationRuleFormData>) => void;
  onRemove: () => void;
  setSubject: (subject: string) => void;
}

const DYNAMIC_VARIABLES = [
  { label: "🏢 Company Name", value: "{company_name}" },
  { label: "📧 User Email", value: "{user_email}" },
  { label: "💬 User First Message", value: "{user_first_message}" },
  { label: "🔐 Is Authenticated", value: "{is_authenticated}" },
];

export function SubjectSection({
  value,
  onChange,
  onRemove,
  setSubject,
}: SubjectSectionProps) {
  const getCurrentSubject = () => {
    const subjectOperation = value.escalationRuleOperations.find(
      (operation) => operation.type === EscalationRuleOperationType.ADD_SUBJECT
    );
    return subjectOperation?.value || "";
  };

  const currentSubject = getCurrentSubject();

  const handleSubjectChange = useCallback(
    (newSubject: string) => {
      setSubject(newSubject);

      onChange({ subject: newSubject });

      const existingSubjectOperation = value.escalationRuleOperations.find(
        (operation) =>
          operation.type === EscalationRuleOperationType.ADD_SUBJECT
      );

      if (existingSubjectOperation) {
        const updatedOperations = value.escalationRuleOperations.map(
          (operation) =>
            operation.type === EscalationRuleOperationType.ADD_SUBJECT
              ? { ...operation, value: newSubject }
              : operation
        );
        onChange({ escalationRuleOperations: updatedOperations });
      } else {
        const newOperations = [
          ...value.escalationRuleOperations,
          {
            type: EscalationRuleOperationType.ADD_SUBJECT,
            enabled: true,
            key: null,
            value: newSubject,
          },
        ];
        onChange({ escalationRuleOperations: newOperations });
      }
    },
    [value.escalationRuleOperations, onChange, setSubject]
  );

  const handleVariableClick = useCallback(
    (variable: string) => {
      const newSubject = currentSubject + variable;
      handleSubjectChange(newSubject);
    },
    [currentSubject, handleSubjectChange]
  );

  const dropdownItems = useMemo(
    () =>
      DYNAMIC_VARIABLES.map((variable) => ({
        label: variable.label,
        onClick: () => handleVariableClick(variable.value),
      })),
    [handleVariableClick]
  );

  return (
    <>
      <Separator
        orientation="horizontal"
        size="4"
        style={{ marginTop: "12px" }}
      />
      <Flex direction={"column"} gap={"2"} mt="4">
        <Flex justify="between" align="center">
          <Text weight="bold" size="2">
            Subject
          </Text>
          <IconButton color="gray" variant="ghost" size="1" onClick={onRemove}>
            <PlusIcon
              width={16}
              height={16}
              style={{ transform: "rotate(45deg)" }}
            />
          </IconButton>
        </Flex>
        <Text size="2" color="gray">
          Set the subject line for escalated tickets
        </Text>

        <Textarea
          placeholder="Enter the subject line..."
          value={currentSubject}
          onChange={(e) => {
            handleSubjectChange(e.target.value);
          }}
          className="min-h-[80px] resize-y"
        />

        <Flex align="center" gap="2">
          {dropdownItems.length > 0 && (
            <DropdownMenu
              items={dropdownItems.map((option) => ({
                key: option.label,
                label: option.label,
                onClick: () => {
                  option.onClick();
                },
                icon: <PlusIcon size={14} />,
              }))}
            >
              <DropdownMenu.Trigger>
                <Button
                  color="gray"
                  variant="surface"
                  highContrast
                  size="2"
                  mb="3"
                >
                  <PlusIcon size={15} />
                  Add Dynamic Variable
                </Button>
              </DropdownMenu.Trigger>
            </DropdownMenu>
          )}
        </Flex>
      </Flex>
    </>
  );
}
